import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowLeft,
  AtSign,
  BadgeMinus,
  ChevronRight,
  DatabaseZap,
  KeyRound,
  LogOut,
  ShieldAlert,
  UserRoundX,
} from 'lucide-react'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { auth } from '../../lib/firebase'
import { useVoxnela } from '../../contexts/VoxnelaContext'
import {
  openSubscriptionManagement,
  startDeletionProtocol,
  type DeletionProtocolResult,
} from '../../lib/accountLifecycle'
import {
  formatMembershipDate,
  membershipPeriodLabel,
  membershipPlanLabel,
} from '../../lib/membership'
import { userInitials } from '../../lib/userInitials'
import { Modal } from '../ui/Modal'
import { useUiText } from '../../i18n/ui'
import { authMessages, formatAuthMessage, type AccountAction } from '../../i18n/auth'
import { guestMessages } from '../../i18n/guest'

type EmailStep = 'email' | 'code'
type ProfileAction = AccountAction
type AuthOperation =
  | 'apple'
  | 'google'
  | 'email-send'
  | 'email-verify'
  | 'logout'
  | null

function formatDeletionDate(value: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Istanbul',
  }).format(new Date(value))
}

function authErrorMessage(cause: unknown, fallback: string, copy: typeof authMessages.en): string {
  const code = String((cause as { code?: string })?.code ?? '').toLowerCase()
  const message = cause instanceof Error ? cause.message : ''
  const combined = `${code} ${message}`.toLowerCase()

  if (combined.includes('popup-closed-by-user') || combined.includes('cancelled-popup-request')) {
    return copy.popupClosed
  }
  if (combined.includes('unauthorized-domain')) {
    return copy.unauthorizedDomain
  }
  if (combined.includes('operation-not-allowed')) {
    return copy.methodUnavailable
  }
  if (combined.includes('resource-exhausted')) {
    return copy.tooManyCodes
  }
  if (combined.includes('invalid-argument') || combined.includes('geçersiz') || combined.includes('expired')) {
    return copy.invalidCode
  }
  if (combined.includes('failed-precondition')) {
    return copy.emailUnavailable
  }
  return message || fallback
}

export function AuthModal({
  open,
  onClose,
  message,
}: {
  open: boolean
  onClose: () => void
  message?: string
}) {
  const { locale, text } = useUiText()
  const copy = authMessages[locale]
  const {
    user,
    isGuest,
    signInGoogle,
    signInApple,
    requestEmailCode,
    verifyEmailCode,
    logout,
  } = useAuth()
  const {
    clearLocalUserData,
    membership,
  } = useVoxnela()
  const [step, setStep] = useState<EmailStep>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [authOperation, setAuthOperation] = useState<AuthOperation>(null)
  const [error, setError] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const [profileAction, setProfileAction] = useState<ProfileAction | null>(null)
  const [profileActionWorking, setProfileActionWorking] = useState(false)
  const [profileActionError, setProfileActionError] = useState('')
  const [deletionResult, setDeletionResult] =
    useState<DeletionProtocolResult | null>(null)
  const lastAutoSubmittedCode = useRef('')
  const verificationInFlight = useRef(false)
  const working = authOperation !== null
  // A guest session is not an account: it sees the optional sign-in instead.
  const account = user && !isGuest ? user : null
  const guestCopy = guestMessages[locale]

  const resetForm = () => {
    setStep('email')
    setCode('')
    setError('')
    setAuthOperation(null)
    setResendCooldown(0)
    setProfileAction(null)
    setProfileActionWorking(false)
    setProfileActionError('')
    setDeletionResult(null)
    lastAutoSubmittedCode.current = ''
    verificationInFlight.current = false
  }

  const closeModal = () => {
    onClose()
    resetForm()
  }

  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = window.setInterval(
      () => setResendCooldown((current) => Math.max(0, current - 1)),
      1000,
    )
    return () => window.clearInterval(timer)
  }, [resendCooldown])

  const runProvider = async (
    operation: () => Promise<void>,
    operationType: Exclude<AuthOperation, 'email-send' | 'email-verify' | null>,
  ) => {
    setAuthOperation(operationType)
    setError('')
    const previousUserId = auth.currentUser?.uid
    try {
      await operation()
      closeModal()
    } catch (cause) {
      // If the user is actually signed in now (e.g. credential succeeded but
      // a follow-up step threw), close the modal silently.
      if (auth.currentUser && auth.currentUser.uid !== previousUserId) {
        closeModal()
        return
      }
      setError(authErrorMessage(cause, copy.signInFailed, copy))
    } finally {
      setAuthOperation(null)
    }
  }

  const sendCode = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault()
    if (working || resendCooldown > 0) return
    const normalizedEmail = email.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError(copy.invalidEmail)
      return
    }

    setAuthOperation('email-send')
    setError('')
    try {
      await requestEmailCode(normalizedEmail)
      setEmail(normalizedEmail)
      setCode('')
      setStep('code')
      setResendCooldown(30)
      lastAutoSubmittedCode.current = ''
    } catch (cause) {
      setError(authErrorMessage(cause, copy.codeSendFailed, copy))
    } finally {
      setAuthOperation(null)
    }
  }

  const verifyCode = async (submittedCode = code) => {
    if (working || verificationInFlight.current) return
    const normalizedCode = submittedCode.replace(/\D/g, '').slice(0, 6)
    if (normalizedCode.length !== 6) {
      setError(copy.enterSixDigitCode)
      return
    }

    verificationInFlight.current = true
    setAuthOperation('email-verify')
    setError('')
    try {
      await verifyEmailCode(email, normalizedCode)
      closeModal()
    } catch (cause) {
      setError(authErrorMessage(cause, copy.codeVerifyFailed, copy))
    } finally {
      verificationInFlight.current = false
      setAuthOperation(null)
    }
  }

  const submitCode = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void verifyCode()
  }

  const updateCode = (value: string) => {
    const normalizedCode = value.replace(/\D/g, '').slice(0, 6)
    setCode(normalizedCode)
    setError('')
    if (
      normalizedCode.length === 6 &&
      normalizedCode !== lastAutoSubmittedCode.current &&
      !working
    ) {
      lastAutoSubmittedCode.current = normalizedCode
      window.setTimeout(() => void verifyCode(normalizedCode), 0)
    }
  }

  const changeEmail = () => {
    setStep('email')
    setCode('')
    setError('')
    setResendCooldown(0)
    lastAutoSubmittedCode.current = ''
  }

  const chooseProfileAction = (action: ProfileAction) => {
    setProfileAction(action)
    setProfileActionError('')
    setDeletionResult(null)
  }

  const dismissProfileAction = () => {
    if (profileActionWorking) return
    const shouldCloseProfile = deletionResult !== null
    setProfileAction(null)
    setProfileActionError('')
    setDeletionResult(null)
    if (shouldCloseProfile) closeModal()
  }

  const confirmProfileAction = async () => {
    if (!user || !profileAction || profileActionWorking) return
    setProfileActionWorking(true)
    setProfileActionError('')
    try {
      if (profileAction === 'subscription') {
        await openSubscriptionManagement(user.uid)
        setProfileAction(null)
        return
      }

      const result = await startDeletionProtocol(profileAction)
      setDeletionResult(result)
      clearLocalUserData()
      try {
        await logout()
      } catch (logoutError) {
        console.warn(
          '[VOXNELA_ACCOUNT] Silme protokolü oluşturuldu; yerel oturum kapatma tamamlanamadı.',
          logoutError,
        )
      }
    } catch (cause) {
      setProfileActionError(
        authErrorMessage(cause, copy.operationFailed, copy),
      )
    } finally {
      setProfileActionWorking(false)
    }
  }

  const selectedActionCopy = profileAction
    ? copy.actions[profileAction]
    : null
  const membershipLabel = membershipPlanLabel(membership, locale)
  const periodLabel = membershipPeriodLabel(membership.billingPeriod, locale)
  const paidMembership = membership.plan !== 'free'

  return (
    <>
      <Modal
        open={open}
        onClose={closeModal}
        title={account ? text('profile') : step === 'email' ? text('login') : copy.enterCode}
        placement="sheet"
        className={`auth-modal${account ? ' auth-modal--account' : ''}`}
      >
      {account ? (
        <div className="auth-account">
          <section
            className="account-profile-summary"
            aria-label={copy.membershipAndAccount}
          >
            <div className="account-membership-copy">
              <div className="account-membership-heading">
                <strong>{membershipLabel}</strong>
                {paidMembership && (
                  <span>
                    {periodLabel ?? copy.noPeriodInfo}
                    {!membership.willRenew && ` · ${text('renewalOff')}`}
                  </span>
                )}
              </div>
              <dl>
                <div>
                  <dt>
                    {paidMembership
                      ? formatAuthMessage(copy.planSince, { plan: membership.plan === 'studio' ? 'Studio' : 'Pro' })
                      : copy.memberSince}
                  </dt>
                  <dd>
                    {formatMembershipDate(membership.planStartedAtMs, locale)}
                  </dd>
                </div>
                {paidMembership && (
                  <div>
                    <dt>{copy.membershipRenewal}</dt>
                    <dd>{formatMembershipDate(membership.renewalAtMs, locale)}</dd>
                  </div>
                )}
              </dl>
            </div>
            <div className="account-identity">
              <span className="auth-avatar">
                {userInitials(account.displayName, account.email)}
              </span>
              <strong>{account.displayName ?? text('user')}</strong>
              <small>{account.email}</small>
            </div>
          </section>
          <div className="account-action-list" aria-label={copy.accountActions}>
            <button
              type="button"
              className="account-action-row"
              onClick={() => chooseProfileAction('subscription')}
            >
              <span className="account-action-icon"><BadgeMinus size={18} /></span>
              <span>
                <strong>{copy.manageSubscriptionTitle}</strong>
                <small>{copy.manageSubscriptionSubtitle}</small>
              </span>
              <ChevronRight size={17} />
            </button>
            <button
              type="button"
              className="account-action-row account-action-row--danger"
              onClick={() => chooseProfileAction('account')}
            >
              <span className="account-action-icon"><UserRoundX size={18} /></span>
              <span>
                <strong>{copy.deleteAccountTitle}</strong>
                <small>{copy.deleteAccountSubtitle}</small>
              </span>
              <ChevronRight size={17} />
            </button>
            <button
              type="button"
              className="account-action-row account-action-row--danger"
              onClick={() => chooseProfileAction('data')}
            >
              <span className="account-action-icon"><DatabaseZap size={18} /></span>
              <span>
                <strong>{copy.deleteDataTitle}</strong>
                <small>{copy.deleteDataSubtitle}</small>
              </span>
              <ChevronRight size={17} />
            </button>
          </div>
          <button
            type="button"
            className="outline-button full-button auth-logout-button"
            onClick={() => void runProvider(logout, 'logout')}
          >
            <LogOut size={17} />
            {authOperation === 'logout' ? copy.signingOut : copy.signOut}
          </button>
        </div>
      ) : (
        <div className="auth-form">
          {message ? (
            <p className="auth-required-message" role="alert">
              {message}
            </p>
          ) : isGuest && (
            <p className="auth-required-message">{guestCopy.guestHint}</p>
          )}
          <AnimatePresence mode="wait" initial={false}>
            {step === 'email' ? (
              <motion.div
                key="email-step"
                className="auth-step"
                initial={{ opacity: 0, x: -18 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 18 }}
                transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
              >
                <form onSubmit={(event) => void sendCode(event)}>
                  <label>
                    {copy.email}
                    <span>
                      <AtSign size={16} />
                      <input
                        name="email"
                        type="email"
                        inputMode="email"
                        enterKeyHint="send"
                        autoComplete="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        required
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value)
                          setError('')
                        }}
                        placeholder={copy.emailPlaceholder}
                      />
                    </span>
                  </label>
                  {error && <p className="auth-error">{error}</p>}
                  <button
                    type="submit"
                    className="primary-button full-button auth-email-submit"
                    disabled={working}
                  >
                    {authOperation === 'email-send'
                      ? copy.sendingCode
                      : copy.emailSignIn}
                  </button>
                </form>
                <div className="auth-divider"><span />{copy.or}<span /></div>
                <button
                  type="button"
                  className="auth-provider auth-provider--apple"
                  disabled={working}
                  aria-label={
                    authOperation === 'apple'
                      ? copy.signingIn
                      : copy.continueApple
                  }
                  onClick={() => void runProvider(signInApple, 'apple')}
                >
                  <img
                    src="/brand/auth/apple-logo-white.png"
                    alt=""
                    aria-hidden="true"
                  />
                  <span>
                    {authOperation === 'apple'
                      ? copy.signingIn
                      : copy.continueApple}
                  </span>
                </button>
                <button
                  type="button"
                  className="auth-provider auth-provider--google"
                  disabled={working}
                  onClick={() => void runProvider(signInGoogle, 'google')}
                >
                  <img
                    src="/brand/auth/google-g.png"
                    alt=""
                    aria-hidden="true"
                  />
                  <span>
                    {authOperation === 'google'
                      ? copy.signingIn
                      : copy.continueGoogle}
                  </span>
                </button>
                <button
                  type="button"
                  className="auth-guest"
                  onClick={closeModal}
                >
                  {copy.continueWithoutSignIn}
                </button>
                <p className="auth-signin-agreement">{copy.signInAgreement}</p>
                <nav className="auth-legal-links" aria-label={copy.legalLinksLabel}>
                  <Link to="/privacy" onClick={closeModal}>{copy.privacyPolicy}</Link>
                  <span aria-hidden="true">·</span>
                  <Link to="/terms" onClick={closeModal}>{copy.termsOfUse}</Link>
                </nav>
              </motion.div>
            ) : (
              <motion.div
                key="code-step"
                className="auth-step auth-code-step"
                initial={{ opacity: 0, x: 18 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -18 }}
                transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
              >
                <div className="auth-code-recipient">
                  <span><KeyRound size={17} />{formatAuthMessage(copy.codeSent, { email })}</span>
                  <button type="button" onClick={changeEmail}><ArrowLeft size={15} /> {copy.change}</button>
                </div>
                <form onSubmit={submitCode}>
                  <label className="auth-code-field">
                    {copy.sixDigitCode}
                    <span>
                      <input
                        name="code"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                        maxLength={6}
                        value={code}
                        onChange={(event) => updateCode(event.target.value)}
                        placeholder="000000"
                        aria-label={copy.emailCode}
                      />
                    </span>
                  </label>
                  {error && <p className="auth-error">{error}</p>}
                </form>
                <button
                  type="button"
                  className="auth-resend"
                  disabled={working || resendCooldown > 0}
                  onClick={() => void sendCode()}
                >
                  {resendCooldown > 0 ? `${copy.resendCode} · ${resendCooldown}` : copy.resendCode}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      </Modal>
      <Modal
        open={profileAction !== null}
        onClose={dismissProfileAction}
        placement="sheet"
        eyebrow={deletionResult ? copy.requestReceived : selectedActionCopy?.eyebrow}
        title={
          deletionResult
            ? deletionResult.type === 'account'
              ? copy.accountDeletionStarted
              : copy.dataDeletionStarted
            : selectedActionCopy?.title ?? copy.accountAction
        }
      >
        {deletionResult ? (
          <div className="account-action-confirmation account-action-confirmation--complete">
            <span className="account-warning-icon"><ShieldAlert size={24} /></span>
            <p>
              {deletionResult.type === 'account'
                ? formatAuthMessage(copy.accountDeletesAt, { date: formatDeletionDate(deletionResult.executeAfterMs, locale) })
                : formatAuthMessage(copy.dataDeletesAt, { date: formatDeletionDate(deletionResult.executeAfterMs, locale) })}
            </p>
            <p>
              {copy.deletionUndo}
            </p>
            <button
              type="button"
              className="primary-button full-button"
              onClick={dismissProfileAction}
            >
              {copy.done}
            </button>
          </div>
        ) : (
          <div className="account-action-confirmation">
            <span className="account-warning-icon"><ShieldAlert size={24} /></span>
            <div className="account-warning-copy">
              {selectedActionCopy?.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
            {profileActionError && <p className="auth-error">{profileActionError}</p>}
            <div className="account-warning-buttons">
              <button
                type="button"
                className="outline-button"
                disabled={profileActionWorking}
                onClick={dismissProfileAction}
              >
                {copy.cancel}
              </button>
              <button
                type="button"
                className={
                  profileAction === 'subscription'
                    ? 'primary-button'
                    : 'danger-button'
                }
                disabled={profileActionWorking}
                onClick={() => void confirmProfileAction()}
              >
                {profileActionWorking
                  ? copy.preparing
                  : selectedActionCopy?.confirm}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}
